import type {
  ApiCampaign,
  ApiCampaignCompletion,
  ApiCampaignService,
  ApiComment,
  ApiCommunityCampaign,
  ApiCommunityMeetup,
  ApiCommunityMember,
  ApiMyCampaignResponse,
  ApiMyCohortResponse,
  ApiPost,
  ApiPostsResponse,
  ApiPublicCampaignResponse,
  ApiSaver,
  ApiUser,
  OnboardingData,
} from '@/lib/api'
import {
  E2E_CLAIM_FAIL_ONCE_KEY,
  E2E_CREATE_USER_FAIL_ONCE_KEY,
  E2E_PATCH_ONBOARDING_FAIL_ONCE_KEY,
  E2E_STATS_FAIL_ONCE_KEY,
  E2E_WAITLIST_FAIL_ONCE_KEY,
  readE2EBooleanFlag,
  writeE2EBooleanFlag,
} from '@/lib/e2e'

type MockOnboardingSession = {
  id: string
  claimed_by: string | null
  goal?: string
  destination?: string
  timeline?: string
  origin_city?: string
  origin_country?: string
  age_range?: string
  savings_status?: string
  current_situation?: string
}

type MockCohortAccess = {
  member_status: string
  created_at: string
  updated_at: string | null
}

type MockPostRecord = {
  id: string
  user_id: string
  author_name: string
  body: string
  created_at: string
  is_admin: boolean
  is_pinned: boolean
}

type MockCommentRecord = {
  id: string
  post_id: string
  user_id: string
  author_name: string
  body: string
  created_at: string
  is_admin: boolean
}

type MockCommunityMeetupRecord = {
  id: string
  title: string
  starts_at: string
  join_url: string
  attendee_user_ids: string[]
}

type MockApiState = {
  usersById: Record<string, ApiUser>
  userIdByEmail: Record<string, string>
  onboardingSessions: Record<string, MockOnboardingSession>
  waitlist: { email: string; onboarding_id?: string }[]
  campaignsByUserId: Record<string, ApiCampaign>
  campaignServicesByUserId: Record<string, ApiCampaignService[]>
  communityMembers: ApiCommunityMember[]
  cohortAccessByUserId: Record<string, MockCohortAccess>
  communityMeetups: MockCommunityMeetupRecord[]
  postsById: Record<string, MockPostRecord>
  postOrder: string[]
  commentsById: Record<string, MockCommentRecord>
  commentIdsByPostId: Record<string, string[]>
  postLikeUserIdsByPostId: Record<string, string[]>
  commentLikeUserIdsByCommentId: Record<string, string[]>
  nextUserSeq: number
  nextCampaignSeq: number
  nextServiceSeq: number
  nextPostSeq: number
  nextCommentSeq: number
  nextMeetupSeq: number
  stats: {
    user_count: number
    max_users: number
    mapbox_max_private_calls: number
  }
}

type CampaignServiceBody = {
  title?: unknown
  description?: unknown
  price?: unknown
  currency?: unknown
  status?: unknown
}

function applyTitlePatch(updated: ApiCampaignService, title: unknown): void {
  if (title === undefined) return
  const trimmed = String(title).trim()
  if (!trimmed) throw createError(400, 'service title is required')
  updated.title = trimmed
}

function applyDescriptionPatch(updated: ApiCampaignService, description: unknown): void {
  if (description === undefined) return
  const trimmed = String(description).trim()
  updated.description = trimmed.length > 0 ? trimmed : null
}

function applyPricePatch(updated: ApiCampaignService, price: unknown): void {
  if (price === undefined) return
  updated.price = typeof price === 'number' && Number.isFinite(price) ? price : null
}

function applyCurrencyPatch(updated: ApiCampaignService, currency: unknown): void {
  if (currency === undefined) return
  const trimmed = String(currency).trim()
  updated.currency = trimmed || updated.currency
}

function applyStatusPatch(updated: ApiCampaignService, status: unknown): void {
  if (status === undefined) return
  const trimmed = String(status).trim()
  updated.status = trimmed || updated.status
}

function applyServicePatch(
  service: ApiCampaignService,
  body: CampaignServiceBody
): ApiCampaignService {
  const updated = { ...service }

  applyTitlePatch(updated, body.title)
  applyDescriptionPatch(updated, body.description)
  applyPricePatch(updated, body.price)
  applyCurrencyPatch(updated, body.currency)
  applyStatusPatch(updated, body.status)

  return updated
}

function createError(status: number, message: string): Error & { status: number } {
  const error = new Error(message) as Error & { status: number }
  error.status = status
  return error
}

type Cursor = {
  before_created_at: string
  before_id: string
}

const STATE_KEY = 'turbina_e2e_api_state'

function hasLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function nowIso(): string {
  return new Date().toISOString()
}

function base64UrlDecode(value: string): string {
  if (typeof globalThis.atob !== 'function') return ''
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  return globalThis.atob(padded)
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    return JSON.parse(base64UrlDecode(parts[1])) as Record<string, unknown>
  } catch {
    return null
  }
}

function extractEmailFromToken(token: string | null): string | null {
  if (!token) return null
  const payload = decodeJwtPayload(token)
  if (!payload) return null
  const email = payload.email
  return typeof email === 'string' ? email.toLowerCase() : null
}

function parseBody<T>(options: RequestInit): T {
  if (typeof options.body !== 'string' || !options.body) {
    return {} as T
  }
  try {
    return JSON.parse(options.body) as T
  } catch {
    return {} as T
  }
}

function createDefaultUser(params: {
  id: string
  email: string
  name?: string
  surname?: string
  phone?: string
}): ApiUser {
  const ts = nowIso()
  return {
    id: params.id,
    cognito_id: `mock-cognito-${params.id}`,
    email: params.email,
    name: params.name ?? 'E2E',
    surname: params.surname ?? 'User',
    phone: params.phone ?? null,
    instagram: null,
    onboarding_goal: '',
    onboarding_destination: '',
    onboarding_timeline: '',
    origin_city: '',
    origin_country: '',
    age_range: '',
    savings_status: '',
    current_situation: '',
    application_status: 'approved',
    application_submission_type: '',
    application_video_url: '',
    application_written_why_trip: '',
    application_written_limitations: '',
    application_written_experience_goal: '',
    application_written_why_generation_one: '',
    application_submitted_at: null,
    departure: null,
    created_at: ts,
  }
}

function normalizeServiceMap(
  input: Record<string, ApiCampaignService[]> | undefined
): Record<string, ApiCampaignService[]> {
  if (!input) return {}
  const normalized: Record<string, ApiCampaignService[]> = {}
  for (const [userId, services] of Object.entries(input)) {
    normalized[userId] = Array.isArray(services) ? services : []
  }
  return normalized
}

function normalizeArray<T>(input: T[] | undefined): T[] {
  return Array.isArray(input) ? input : []
}

function normalizeRecord<T>(input: Record<string, T> | undefined): Record<string, T> {
  return input ?? {}
}

function normalizeMeetups(
  input: MockCommunityMeetupRecord[] | undefined
): MockCommunityMeetupRecord[] {
  if (!Array.isArray(input)) return []
  return input.map((meetup) => ({
    ...meetup,
    attendee_user_ids: Array.isArray(meetup.attendee_user_ids) ? meetup.attendee_user_ids : [],
  }))
}

function defaultPostsResponse(): ApiPostsResponse {
  return {
    posts: [] as ApiPost[],
    next_cursor: null,
  }
}

function createDefaultState(): MockApiState {
  return {
    usersById: {},
    userIdByEmail: {},
    onboardingSessions: {},
    waitlist: [],
    campaignsByUserId: {},
    campaignServicesByUserId: {},
    communityMembers: [],
    cohortAccessByUserId: {},
    communityMeetups: [],
    postsById: {},
    postOrder: [],
    commentsById: {},
    commentIdsByPostId: {},
    postLikeUserIdsByPostId: {},
    commentLikeUserIdsByCommentId: {},
    nextUserSeq: 1,
    nextCampaignSeq: 1,
    nextServiceSeq: 1,
    nextPostSeq: 1,
    nextCommentSeq: 1,
    nextMeetupSeq: 1,
    stats: {
      user_count: 0,
      max_users: 100,
      mapbox_max_private_calls: 1000,
      feature_dual_application_paths: true,
    },
  }
}

function withStateDefaults(input: MockApiState | null | undefined): MockApiState {
  const defaults = createDefaultState()
  if (!input) return defaults

  return {
    ...defaults,
    ...input,
    usersById: normalizeRecord(input.usersById),
    userIdByEmail: normalizeRecord(input.userIdByEmail),
    onboardingSessions: normalizeRecord(input.onboardingSessions),
    waitlist: normalizeArray(input.waitlist),
    campaignsByUserId: normalizeRecord(input.campaignsByUserId),
    campaignServicesByUserId: normalizeServiceMap(input.campaignServicesByUserId),
    communityMembers: normalizeArray(input.communityMembers),
    cohortAccessByUserId: normalizeRecord(input.cohortAccessByUserId),
    communityMeetups: normalizeMeetups(input.communityMeetups),
    postsById: normalizeRecord(input.postsById),
    postOrder: normalizeArray(input.postOrder),
    commentsById: normalizeRecord(input.commentsById),
    commentIdsByPostId: normalizeRecord(input.commentIdsByPostId),
    postLikeUserIdsByPostId: normalizeRecord(input.postLikeUserIdsByPostId),
    commentLikeUserIdsByCommentId: normalizeRecord(input.commentLikeUserIdsByCommentId),
    stats: {
      ...defaults.stats,
      ...input.stats,
    },
  }
}

function loadState(): MockApiState {
  if (!hasLocalStorage()) return createDefaultState()
  const raw = window.localStorage.getItem(STATE_KEY)
  if (!raw) return createDefaultState()
  try {
    return withStateDefaults(JSON.parse(raw) as MockApiState)
  } catch {
    return createDefaultState()
  }
}

function saveState(state: MockApiState): void {
  if (!hasLocalStorage()) return
  window.localStorage.setItem(STATE_KEY, JSON.stringify(state))
}

function updateUserCount(state: MockApiState): void {
  state.stats.user_count = Object.keys(state.userIdByEmail).length
}

function requireAuthenticatedUser(state: MockApiState, token: string | null): ApiUser {
  const email = extractEmailFromToken(token)
  if (!email) throw createError(401, 'unauthorized')
  const userId = state.userIdByEmail[email]
  if (!userId) throw createError(404, 'user not found')
  const user = state.usersById[userId]
  if (!user) throw createError(404, 'user not found')
  return user
}

function upsertOnboardingSession(
  state: MockApiState,
  sessionId: string,
  data: OnboardingData
): MockOnboardingSession {
  const current = state.onboardingSessions[sessionId] ?? {
    id: sessionId,
    claimed_by: null,
  }
  const updated: MockOnboardingSession = {
    ...current,
    ...data,
  }
  state.onboardingSessions[sessionId] = updated
  return updated
}

function applySessionToUser(user: ApiUser, session: MockOnboardingSession): ApiUser {
  return {
    ...user,
    onboarding_goal: session.goal ?? user.onboarding_goal,
    onboarding_destination: session.destination ?? user.onboarding_destination,
    onboarding_timeline: session.timeline ?? user.onboarding_timeline,
    origin_city: session.origin_city ?? user.origin_city,
    origin_country: session.origin_country ?? user.origin_country,
    age_range: session.age_range ?? user.age_range,
    savings_status: session.savings_status ?? user.savings_status,
    current_situation: session.current_situation ?? user.current_situation,
  }
}

function getCampaignServicesForUser(state: MockApiState, userId: string): ApiCampaignService[] {
  const existing = state.campaignServicesByUserId[userId]
  if (Array.isArray(existing)) return existing
  state.campaignServicesByUserId[userId] = []
  return state.campaignServicesByUserId[userId]
}

function createDefaultCampaignForUser(state: MockApiState, user: ApiUser): ApiCampaign {
  const now = nowIso()
  const campaign: ApiCampaign = {
    id: `campaign-${state.nextCampaignSeq}`,
    user_id: user.id,
    title: 'My Campaign',
    youtube_url: null,
    status: 'planning',
    is_published: false,
    story_who_are_you: '',
    short_id: null,
    created_at: now,
    updated_at: now,
  }
  state.nextCampaignSeq += 1
  state.campaignsByUserId[user.id] = campaign
  return campaign
}

function buildCampaignCompletion(
  campaign: ApiCampaign | null,
  services: ApiCampaignService[]
): ApiCampaignCompletion {
  if (!campaign) {
    return {
      required_prompts_completed: false,
      has_video: false,
      has_service: false,
      is_complete: false,
      missing_fields: ['story_who_are_you', 'services'],
    }
  }

  const hasStory = campaign.story_who_are_you.trim().length > 0
  const hasVideo = Boolean(campaign.youtube_url?.trim())
  const hasService = services.some((service) => service.status === 'active')
  const missingFields: string[] = []

  if (!hasStory) missingFields.push('story_who_are_you')
  if (!hasService) missingFields.push('services')

  return {
    required_prompts_completed: hasStory,
    has_video: hasVideo,
    has_service: hasService,
    is_complete: hasStory && hasService,
    missing_fields: missingFields,
  }
}

function buildMyCampaignResponse(state: MockApiState, userId: string): ApiMyCampaignResponse {
  const campaign = state.campaignsByUserId[userId] ?? null
  const services = getCampaignServicesForUser(state, userId)
  return {
    campaign,
    services,
    completion: buildCampaignCompletion(campaign, services),
  }
}

function normalizeShortId(input: string | null | undefined): string | null {
  if (!input) return null
  const normalized = input.trim().toLowerCase()
  return normalized.length > 0 ? normalized : null
}

function generateShortId(state: MockApiState): string {
  const next = String(state.nextCampaignSeq).padStart(4, '0')
  return `e2e${next}`
}

function parsePositiveInt(value: string | null, fallback: number): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return Math.floor(parsed)
}

function buildApiPost(
  state: MockApiState,
  post: MockPostRecord,
  currentUserId: string | null
): ApiPost {
  const likeUserIds = state.postLikeUserIdsByPostId[post.id] ?? []
  const commentIds = state.commentIdsByPostId[post.id] ?? []
  return {
    ...post,
    like_count: likeUserIds.length,
    liked_by_me: currentUserId ? likeUserIds.includes(currentUserId) : false,
    comment_count: commentIds.length,
  }
}

function buildApiComment(
  state: MockApiState,
  comment: MockCommentRecord,
  currentUserId: string | null
): ApiComment {
  const likeUserIds = state.commentLikeUserIdsByCommentId[comment.id] ?? []
  return {
    ...comment,
    like_count: likeUserIds.length,
    liked_by_me: currentUserId ? likeUserIds.includes(currentUserId) : false,
  }
}

function paginateOrderedIds(
  orderedIds: string[],
  beforeId: string | null,
  limit: number
): {
  pageIds: string[]
  hasNext: boolean
} {
  let startIndex = 0
  if (beforeId) {
    const idx = orderedIds.indexOf(beforeId)
    if (idx >= 0) {
      startIndex = idx + 1
    }
  }

  const pageIds = orderedIds.slice(startIndex, startIndex + limit)
  const hasNext = startIndex + limit < orderedIds.length

  return {
    pageIds,
    hasNext,
  }
}

function ensureCohortAccessForUser(state: MockApiState, userId: string): MockCohortAccess | null {
  const existing = state.cohortAccessByUserId[userId]
  if (existing) return existing

  const fromMember = state.communityMembers.find((member) => member.user_id === userId)
  if (!fromMember) return null

  const derived: MockCohortAccess = {
    member_status: fromMember.status,
    created_at: nowIso(),
    updated_at: null,
  }
  state.cohortAccessByUserId[userId] = derived
  return derived
}

function buildCommunityMeetupResponse(
  meetup: MockCommunityMeetupRecord,
  currentUserId: string
): ApiCommunityMeetup {
  const joinedByMe = meetup.attendee_user_ids.includes(currentUserId)
  return {
    id: meetup.id,
    title: meetup.title,
    starts_at: meetup.starts_at,
    join_url: joinedByMe ? meetup.join_url : null,
    attendee_count: meetup.attendee_user_ids.length,
    joined_by_me: joinedByMe,
  }
}

function readClaimFailureFlag(): boolean {
  return readE2EBooleanFlag(E2E_CLAIM_FAIL_ONCE_KEY)
}

function consumeClaimFailureFlag(): void {
  writeE2EBooleanFlag(E2E_CLAIM_FAIL_ONCE_KEY, false)
}

function readCreateUserFailureFlag(): boolean {
  return readE2EBooleanFlag(E2E_CREATE_USER_FAIL_ONCE_KEY)
}

function consumeCreateUserFailureFlag(): void {
  writeE2EBooleanFlag(E2E_CREATE_USER_FAIL_ONCE_KEY, false)
}

function readPatchOnboardingFailureFlag(): boolean {
  return readE2EBooleanFlag(E2E_PATCH_ONBOARDING_FAIL_ONCE_KEY)
}

function consumePatchOnboardingFailureFlag(): void {
  writeE2EBooleanFlag(E2E_PATCH_ONBOARDING_FAIL_ONCE_KEY, false)
}

function readStatsFailureFlag(): boolean {
  return readE2EBooleanFlag(E2E_STATS_FAIL_ONCE_KEY)
}

function consumeStatsFailureFlag(): void {
  writeE2EBooleanFlag(E2E_STATS_FAIL_ONCE_KEY, false)
}

function readWaitlistFailureFlag(): boolean {
  return readE2EBooleanFlag(E2E_WAITLIST_FAIL_ONCE_KEY)
}

function consumeWaitlistFailureFlag(): void {
  writeE2EBooleanFlag(E2E_WAITLIST_FAIL_ONCE_KEY, false)
}

/** Persist user to state and enforce the simulate-failure flag. */
function finalizeUser(state: MockApiState, user: ApiUser): ApiUser {
  state.usersById[user.id] = user
  state.userIdByEmail[user.email] = user.id
  updateUserCount(state)

  if (readCreateUserFailureFlag()) {
    consumeCreateUserFailureFlag()
    throw createError(500, 'simulated create user failure')
  }

  return user
}

/** Attempt to claim an onboarding session. Returns the upgraded user or null. */
function tryClaimOnboardingSession(
  state: MockApiState,
  user: ApiUser,
  sessionId: string
): ApiUser | null {
  const session = state.onboardingSessions[sessionId]
  if (!session) return null
  if (session.claimed_by && session.claimed_by !== user.id) return null

  // Respect the claim-failure flag so tests can simulate claim errors.
  if (readClaimFailureFlag()) {
    consumeClaimFailureFlag()
    // Claim fails but user is still created (mirrors backend behaviour
    // where CreateUser logs the error and continues).
    return null
  }

  session.claimed_by = user.id
  state.onboardingSessions[sessionId] = session
  return applySessionToUser(user, session)
}

function handleCreateUser(state: MockApiState, options: RequestInit): ApiUser {
  const body = parseBody<{
    email?: string
    name?: string
    surname?: string
    phone?: string
    onboarding_session_id?: string
  }>(options)
  const email = body.email?.toLowerCase().trim()
  if (!email) throw createError(400, 'email is required')

  const existingId = state.userIdByEmail[email]
  if (existingId && state.usersById[existingId]) {
    return state.usersById[existingId]
  }

  // Enforce user cap for new users (mirrors backend ErrUserCapReached → 409)
  if (state.stats.user_count >= state.stats.max_users) {
    throw createError(
      409,
      '{"error":"user_cap_reached","applications_open":false,"message":"applications are currently closed"}'
    )
  }

  const id = `e2e-user-${state.nextUserSeq}`
  state.nextUserSeq += 1

  const user = createDefaultUser({
    id,
    email,
    name: body.name,
    surname: body.surname,
    phone: body.phone,
  })

  // Mirror backend CreateUser: claim onboarding session if provided.
  // With auto-approve the backend does this atomically; the e2e mock
  // replicates it so tests see the same onboarding_goal on the user.
  if (body.onboarding_session_id) {
    const claimed = tryClaimOnboardingSession(state, user, body.onboarding_session_id)
    if (claimed) return finalizeUser(state, claimed)
  }

  return finalizeUser(state, user)
}

type PatchableUserStringField =
  | 'application_submission_type'
  | 'application_video_url'
  | 'application_written_why_trip'
  | 'application_written_limitations'
  | 'application_written_experience_goal'
  | 'application_written_why_generation_one'
  | 'application_status'
  | 'name'
  | 'surname'
  | 'onboarding_goal'
  | 'onboarding_destination'
  | 'onboarding_timeline'
  | 'age_range'
  | 'origin_city'
  | 'origin_country'
  | 'savings_status'
  | 'current_situation'

const PATCHABLE_USER_STRING_FIELDS: PatchableUserStringField[] = [
  'application_submission_type',
  'application_video_url',
  'application_written_why_trip',
  'application_written_limitations',
  'application_written_experience_goal',
  'application_written_why_generation_one',
  'application_status',
  'name',
  'surname',
  'onboarding_goal',
  'onboarding_destination',
  'onboarding_timeline',
  'age_range',
  'origin_city',
  'origin_country',
  'savings_status',
  'current_situation',
]

function assignStringFieldIfDefined(
  user: ApiUser,
  body: Partial<ApiUser>,
  field: PatchableUserStringField
): void {
  const value = body[field]
  if (value !== undefined) {
    user[field] = String(value)
  }
}

function assignPhoneIfDefined(user: ApiUser, phone: ApiUser['phone'] | undefined): void {
  if (phone === undefined) {
    return
  }
  user.phone = phone ? String(phone) : null
}

function handlePatchMe(state: MockApiState, token: string | null, options: RequestInit): ApiUser {
  const currentUser = requireAuthenticatedUser(state, token)
  const body = parseBody<Partial<ApiUser>>(options)

  const updated: ApiUser = { ...currentUser }

  for (const field of PATCHABLE_USER_STRING_FIELDS) {
    assignStringFieldIfDefined(updated, body, field)
  }
  assignPhoneIfDefined(updated, body.phone)

  const submittedNow =
    updated.application_submission_type.trim().length > 0 ||
    updated.application_video_url.trim().length > 0 ||
    updated.application_written_why_trip.trim().length > 0 ||
    updated.application_written_limitations.trim().length > 0 ||
    updated.application_written_experience_goal.trim().length > 0 ||
    updated.application_written_why_generation_one.trim().length > 0
  if (submittedNow) {
    updated.application_submitted_at = nowIso()
  }

  state.usersById[currentUser.id] = updated
  return updated
}

function handleClaimSession(state: MockApiState, sessionId: string, token: string | null): ApiUser {
  if (readClaimFailureFlag()) {
    consumeClaimFailureFlag()
    throw createError(500, 'simulated claim failure')
  }

  const currentUser = requireAuthenticatedUser(state, token)
  const session = state.onboardingSessions[sessionId]
  if (!session) throw createError(404, 'onboarding session not found')

  if (session.claimed_by && session.claimed_by !== currentUser.id) {
    throw createError(409, 'onboarding session already claimed')
  }

  session.claimed_by = currentUser.id
  state.onboardingSessions[sessionId] = session

  const updatedUser = applySessionToUser(currentUser, session)
  state.usersById[currentUser.id] = updatedUser
  return updatedUser
}

function buildPublicCampaign(
  state: MockApiState,
  shortId: string
): ApiPublicCampaignResponse | null {
  const campaign = Object.values(state.campaignsByUserId).find(
    (item) => item.is_published && normalizeShortId(item.short_id) === normalizeShortId(shortId)
  )
  if (!campaign) return null

  const owner = state.usersById[campaign.user_id]
  if (!owner) return null

  const services = getCampaignServicesForUser(state, campaign.user_id).filter(
    (service) => service.status === 'active'
  )

  return {
    short_id: campaign.short_id ?? shortId,
    title: campaign.title,
    name: owner.name,
    surname: owner.surname,
    youtube_url: campaign.youtube_url,
    story_who_are_you: campaign.story_who_are_you,
    phone: owner.phone,
    instagram: owner.instagram,
    services,
    goal_total: 0,
    savings_amount: 0,
    departure: owner.departure,
  }
}

// eslint-disable-next-line complexity
export async function e2eMockApiFetch<T>(
  path: string,
  options: RequestInit = {},
  token: string | null
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  const requestUrl = new URL(path, 'https://e2e.local')
  const pathname = requestUrl.pathname
  const state = loadState()

  try {
    if (pathname === '/stats' && method === 'GET') {
      if (readStatsFailureFlag()) {
        consumeStatsFailureFlag()
        throw createError(500, 'simulated stats failure')
      }
      return state.stats as T
    }

    if (pathname === '/waitlist' && method === 'POST') {
      if (readWaitlistFailureFlag()) {
        consumeWaitlistFailureFlag()
        throw createError(500, 'simulated waitlist failure')
      }
      const body = parseBody<{ email?: string; onboarding_id?: string }>(options)
      const email = body.email?.toLowerCase().trim()
      if (!email) throw createError(400, 'email is required')
      const onboardingId = body.onboarding_id?.trim() || undefined
      const existing = state.waitlist.find((w) => w.email === email)
      if (!existing) {
        state.waitlist.push({ email, onboarding_id: onboardingId })
      } else if (onboardingId) {
        existing.onboarding_id = onboardingId
      }
      return undefined as T
    }

    if (pathname === '/users' && method === 'POST') {
      return handleCreateUser(state, options) as T
    }

    if (pathname === '/me' && method === 'GET') {
      return requireAuthenticatedUser(state, token) as T
    }

    if (pathname === '/me' && method === 'PATCH') {
      return handlePatchMe(state, token, options) as T
    }

    const patchSessionMatch = pathname.match(/^\/onboarding\/sessions\/([^/]+)$/)
    if (patchSessionMatch && method === 'PATCH') {
      if (readPatchOnboardingFailureFlag()) {
        consumePatchOnboardingFailureFlag()
        throw createError(500, 'simulated onboarding patch failure')
      }
      const sessionId = decodeURIComponent(patchSessionMatch[1])
      const body = parseBody<OnboardingData>(options)
      upsertOnboardingSession(state, sessionId, body)
      return undefined as T
    }

    const claimSessionMatch = pathname.match(/^\/onboarding\/sessions\/([^/]+)\/claim$/)
    if (claimSessionMatch && method === 'POST') {
      const sessionId = decodeURIComponent(claimSessionMatch[1])
      return handleClaimSession(state, sessionId, token) as T
    }

    if (pathname.startsWith('/campaigns/') && method === 'GET') {
      const shortId = decodeURIComponent(pathname.replace('/campaigns/', ''))
      const campaign = buildPublicCampaign(state, shortId)
      if (!campaign) throw createError(404, 'campaign not found')
      return campaign as T
    }

    if (pathname === '/me/campaign' && method === 'GET') {
      const user = requireAuthenticatedUser(state, token)
      return buildMyCampaignResponse(state, user.id) as T
    }

    if (pathname === '/me/campaign' && method === 'PUT') {
      const user = requireAuthenticatedUser(state, token)
      const body = parseBody<CampaignUpdateBody>(options)
      const campaign = state.campaignsByUserId[user.id] ?? createDefaultCampaignForUser(state, user)

      if (body.title !== undefined) {
        campaign.title = String(body.title).trim() || campaign.title
      }
      if (body.youtube_url !== undefined) {
        const normalized = String(body.youtube_url).trim()
        campaign.youtube_url = normalized.length > 0 ? normalized : null
      }
      if (body.story_who_are_you !== undefined) {
        campaign.story_who_are_you = String(body.story_who_are_you).trim()
      }
      if (body.short_id !== undefined) {
        campaign.short_id = normalizeShortId(body.short_id)
      }

      campaign.updated_at = nowIso()
      state.campaignsByUserId[user.id] = campaign

      return buildMyCampaignResponse(state, user.id) as T
    }

    if (pathname === '/me/campaign/publish' && method === 'POST') {
      const user = requireAuthenticatedUser(state, token)
      const campaign = state.campaignsByUserId[user.id] ?? createDefaultCampaignForUser(state, user)
      const services = getCampaignServicesForUser(state, user.id)
      const completion = buildCampaignCompletion(campaign, services)

      if (!completion.is_complete) {
        throw createError(400, 'campaign is incomplete')
      }

      campaign.is_published = true
      campaign.status = 'active'
      if (!normalizeShortId(campaign.short_id)) {
        campaign.short_id = generateShortId(state)
      }
      campaign.updated_at = nowIso()
      state.campaignsByUserId[user.id] = campaign

      return buildMyCampaignResponse(state, user.id) as T
    }

    if (pathname === '/me/campaign/services' && method === 'GET') {
      const user = requireAuthenticatedUser(state, token)
      return getCampaignServicesForUser(state, user.id) as T
    }

    if (pathname === '/me/campaign/services' && method === 'POST') {
      const user = requireAuthenticatedUser(state, token)
      const body = parseBody<CampaignServiceBody>(options)
      const title = String(body.title ?? '').trim()
      if (!title) throw createError(400, 'service title is required')

      const campaign = state.campaignsByUserId[user.id] ?? createDefaultCampaignForUser(state, user)
      const services = getCampaignServicesForUser(state, user.id)
      const now = nowIso()
      const service: ApiCampaignService = {
        id: `svc-${state.nextServiceSeq}`,
        user_id: user.id,
        project_id: campaign.id,
        title,
        description: body.description?.trim() ? body.description.trim() : null,
        price: typeof body.price === 'number' && Number.isFinite(body.price) ? body.price : null,
        currency: body.currency?.trim() || 'AUD',
        status: 'active',
        created_at: now,
        updated_at: now,
      }

      state.nextServiceSeq += 1
      services.push(service)
      state.campaignServicesByUserId[user.id] = services
      return service as T
    }

    if (pathname.startsWith('/me/campaign/services/') && method === 'PATCH') {
      const user = requireAuthenticatedUser(state, token)
      const serviceId = decodeURIComponent(pathname.split('/').pop() ?? '')
      const services = getCampaignServicesForUser(state, user.id)
      const index = services.findIndex((service) => service.id === serviceId)
      if (index === -1) throw createError(404, 'service not found')

      const body = parseBody<CampaignServiceBody>(options)
      const updated = applyServicePatch(services[index], body)

      updated.updated_at = nowIso()
      services[index] = updated
      state.campaignServicesByUserId[user.id] = services
      return updated as T
    }

    if (pathname.startsWith('/me/campaign/services/') && method === 'DELETE') {
      const user = requireAuthenticatedUser(state, token)
      const serviceId = decodeURIComponent(pathname.split('/').pop() ?? '')
      state.campaignServicesByUserId[user.id] = getCampaignServicesForUser(state, user.id).filter(
        (service) => service.id !== serviceId
      )
      return undefined as T
    }

    if (pathname === '/me/goals' && method === 'GET') {
      return [] as T
    }

    if (pathname === '/me/goals' && method === 'PUT') {
      return [] as T
    }

    if (pathname.startsWith('/me/goals/') && method === 'PATCH') {
      const name = decodeURIComponent(pathname.split('/').pop() ?? 'goal')
      return {
        id: `goal-${name}`,
        name,
        amount: 0,
        currency: 'AUD',
      } as T
    }

    if (pathname === '/me/saver' && method === 'GET') {
      const user = requireAuthenticatedUser(state, token)
      const saver: ApiSaver = {
        id: `saver-${user.id}`,
        user_id: user.id,
        amount: 0,
        currency: 'AUD',
      }
      return saver as T
    }

    if (pathname === '/me/saver' && method === 'PUT') {
      const user = requireAuthenticatedUser(state, token)
      const body = parseBody<{ amount?: number }>(options)
      const saver: ApiSaver = {
        id: `saver-${user.id}`,
        user_id: user.id,
        amount: body.amount ?? 0,
        currency: 'AUD',
      }
      return saver as T
    }

    if (pathname === '/community/posts' && method === 'GET') {
      const currentUser = requireAuthenticatedUser(state, token)
      const orderedIds = state.postOrder.filter((id) => Boolean(state.postsById[id]))
      const limit = parsePositiveInt(requestUrl.searchParams.get('limit'), 20)
      const beforeId = requestUrl.searchParams.get('before_id')
      const { pageIds, hasNext } = paginateOrderedIds(orderedIds, beforeId, limit)
      const tailId = pageIds[pageIds.length - 1]
      const nextCursor: Cursor | null =
        hasNext && tailId
          ? {
              before_created_at: state.postsById[tailId]?.created_at ?? nowIso(),
              before_id: tailId,
            }
          : null

      const posts = pageIds
        .map((id) => state.postsById[id])
        .filter((post): post is MockPostRecord => Boolean(post))
        .map((post) => buildApiPost(state, post, currentUser.id))

      return {
        posts,
        next_cursor: nextCursor,
      } as T
    }

    if (pathname.startsWith('/community/posts/search') && method === 'GET') {
      const currentUser = requireAuthenticatedUser(state, token)
      const query = requestUrl.searchParams.get('q')?.trim().toLowerCase() ?? ''
      if (!query) return defaultPostsResponse() as T

      const filteredIds = state.postOrder.filter((id) => {
        const post = state.postsById[id]
        if (!post) return false
        return (
          post.body.toLowerCase().includes(query) || post.author_name.toLowerCase().includes(query)
        )
      })

      const limit = parsePositiveInt(requestUrl.searchParams.get('limit'), 20)
      const beforeId = requestUrl.searchParams.get('before_id')
      const { pageIds, hasNext } = paginateOrderedIds(filteredIds, beforeId, limit)
      const tailId = pageIds[pageIds.length - 1]
      const nextCursor: Cursor | null =
        hasNext && tailId
          ? {
              before_created_at: state.postsById[tailId]?.created_at ?? nowIso(),
              before_id: tailId,
            }
          : null

      const posts = pageIds
        .map((id) => state.postsById[id])
        .filter((post): post is MockPostRecord => Boolean(post))
        .map((post) => buildApiPost(state, post, currentUser.id))

      return {
        posts,
        next_cursor: nextCursor,
      } as T
    }

    if (pathname === '/community/posts' && method === 'POST') {
      const body = parseBody<{ body?: string }>(options)
      const user = requireAuthenticatedUser(state, token)
      const trimmedBody = String(body.body ?? '').trim()
      if (!trimmedBody) throw createError(400, 'body is required')

      const post: MockPostRecord = {
        id: `post-${state.nextPostSeq}`,
        user_id: user.id,
        author_name: `${user.name} ${user.surname}`.trim(),
        body: trimmedBody,
        created_at: nowIso(),
        is_admin: false,
        is_pinned: false,
      }

      state.nextPostSeq += 1
      state.postsById[post.id] = post
      state.postOrder = [post.id, ...state.postOrder.filter((id) => id !== post.id)]

      return buildApiPost(state, post, user.id) as T
    }

    if (pathname === '/community/members' && method === 'GET') {
      return {
        cohort_id: 'e2e-cohort',
        members: state.communityMembers,
      } as T
    }

    if (pathname === '/community/campaigns' && method === 'GET') {
      const selectedMemberIds = new Set(
        state.communityMembers
          .filter((member) => member.status === 'selected')
          .map((member) => member.user_id)
      )

      const campaigns: ApiCommunityCampaign[] = Object.values(state.campaignsByUserId)
        .filter((campaign) => Boolean(normalizeShortId(campaign.short_id)))
        .filter(
          (campaign) => selectedMemberIds.size === 0 || selectedMemberIds.has(campaign.user_id)
        )
        .map((campaign) => {
          const user = state.usersById[campaign.user_id]
          const activeServiceCount = getCampaignServicesForUser(state, campaign.user_id).filter(
            (service) => service.status === 'active'
          ).length

          return {
            id: campaign.id,
            user_id: campaign.user_id,
            short_id: campaign.short_id ?? '',
            title: campaign.title,
            name: user?.name ?? 'E2E',
            surname: user?.surname ?? 'User',
            active_service_count: activeServiceCount,
            is_published: campaign.is_published,
            created_at: campaign.created_at,
          }
        })
        .sort((a, b) => b.created_at.localeCompare(a.created_at))

      return campaigns as T
    }

    if (pathname === '/community/stats' && method === 'GET') {
      const approvedUsers = Object.values(state.usersById).filter(
        (user) => user.application_status === 'approved'
      )
      const activeCampaigns = Object.values(state.campaignsByUserId).filter(
        (campaign) => campaign.is_published
      )

      return {
        approved_users_count: approvedUsers.length,
        approved_users_total_saved: 0,
        active_campaigns_count: activeCampaigns.length,
      } as T
    }

    if (pathname === '/community/meetups' && method === 'GET') {
      const currentUser = requireAuthenticatedUser(state, token)
      return [...state.communityMeetups]
        .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
        .map((meetup) => buildCommunityMeetupResponse(meetup, currentUser.id)) as T
    }

    const joinMeetupMatch = pathname.match(/^\/community\/meetups\/([^/]+)\/join$/)
    if (joinMeetupMatch && method === 'POST') {
      const meetupId = decodeURIComponent(joinMeetupMatch[1])
      const currentUser = requireAuthenticatedUser(state, token)
      const meetup = state.communityMeetups.find((entry) => entry.id === meetupId)
      if (!meetup) throw createError(404, 'meetup not found')
      if (!meetup.attendee_user_ids.includes(currentUser.id)) {
        meetup.attendee_user_ids.push(currentUser.id)
      }
      return undefined as T
    }

    if (joinMeetupMatch && method === 'DELETE') {
      const meetupId = decodeURIComponent(joinMeetupMatch[1])
      const currentUser = requireAuthenticatedUser(state, token)
      const meetup = state.communityMeetups.find((entry) => entry.id === meetupId)
      if (!meetup) throw createError(404, 'meetup not found')
      meetup.attendee_user_ids = meetup.attendee_user_ids.filter((id) => id !== currentUser.id)
      return undefined as T
    }

    if (
      pathname.startsWith('/community/posts/') &&
      pathname.endsWith('/comments') &&
      method === 'GET'
    ) {
      const currentUser = requireAuthenticatedUser(state, token)
      const postId = decodeURIComponent(pathname.split('/')[3] ?? '')
      const commentIds = state.commentIdsByPostId[postId] ?? []
      return commentIds
        .map((commentId) => state.commentsById[commentId])
        .filter((comment): comment is MockCommentRecord => Boolean(comment))
        .map((comment) => buildApiComment(state, comment, currentUser.id)) as T
    }

    if (
      pathname.startsWith('/community/posts/') &&
      pathname.endsWith('/comments') &&
      method === 'POST'
    ) {
      const body = parseBody<{ body?: string }>(options)
      const user = requireAuthenticatedUser(state, token)
      const postId = decodeURIComponent(pathname.split('/')[3] ?? '')
      const post = state.postsById[postId]
      if (!post) throw createError(404, 'post not found')

      const trimmedBody = String(body.body ?? '').trim()
      if (!trimmedBody) throw createError(400, 'body is required')

      const comment: MockCommentRecord = {
        id: `comment-${state.nextCommentSeq}`,
        post_id: postId,
        user_id: user.id,
        author_name: `${user.name} ${user.surname}`.trim(),
        body: trimmedBody,
        created_at: nowIso(),
        is_admin: false,
      }

      state.nextCommentSeq += 1
      state.commentsById[comment.id] = comment
      const commentIds = state.commentIdsByPostId[postId] ?? []
      state.commentIdsByPostId[postId] = [...commentIds, comment.id]

      return buildApiComment(state, comment, user.id) as T
    }

    const postLikeMatch = pathname.match(/^\/community\/posts\/([^/]+)\/like$/)
    if (postLikeMatch && method === 'POST') {
      const postId = decodeURIComponent(postLikeMatch[1])
      const currentUser = requireAuthenticatedUser(state, token)
      if (!state.postsById[postId]) throw createError(404, 'post not found')

      const likes = state.postLikeUserIdsByPostId[postId] ?? []
      state.postLikeUserIdsByPostId[postId] = likes.includes(currentUser.id)
        ? likes.filter((id) => id !== currentUser.id)
        : [...likes, currentUser.id]
      return undefined as T
    }

    const postDeleteMatch = pathname.match(/^\/community\/posts\/([^/]+)$/)
    if (postDeleteMatch && method === 'DELETE') {
      const postId = decodeURIComponent(postDeleteMatch[1])
      const currentUser = requireAuthenticatedUser(state, token)
      const post = state.postsById[postId]
      if (!post) return undefined as T
      if (post.user_id !== currentUser.id) throw createError(403, 'forbidden')

      const commentIds = state.commentIdsByPostId[postId] ?? []
      for (const commentId of commentIds) {
        delete state.commentsById[commentId]
        delete state.commentLikeUserIdsByCommentId[commentId]
      }
      delete state.commentIdsByPostId[postId]
      delete state.postLikeUserIdsByPostId[postId]
      delete state.postsById[postId]
      state.postOrder = state.postOrder.filter((id) => id !== postId)

      return undefined as T
    }

    const commentLikeMatch = pathname.match(/^\/community\/comments\/([^/]+)\/like$/)
    if (commentLikeMatch && method === 'POST') {
      const commentId = decodeURIComponent(commentLikeMatch[1])
      const currentUser = requireAuthenticatedUser(state, token)
      if (!state.commentsById[commentId]) throw createError(404, 'comment not found')

      const likes = state.commentLikeUserIdsByCommentId[commentId] ?? []
      state.commentLikeUserIdsByCommentId[commentId] = likes.includes(currentUser.id)
        ? likes.filter((id) => id !== currentUser.id)
        : [...likes, currentUser.id]
      return undefined as T
    }

    if (pathname.startsWith('/community/comments/') && method === 'DELETE') {
      const commentId = decodeURIComponent(pathname.split('/').pop() ?? '')
      const currentUser = requireAuthenticatedUser(state, token)
      const comment = state.commentsById[commentId]
      if (!comment) return undefined as T
      if (comment.user_id !== currentUser.id) throw createError(403, 'forbidden')

      state.commentIdsByPostId[comment.post_id] = (
        state.commentIdsByPostId[comment.post_id] ?? []
      ).filter((id) => id !== commentId)
      delete state.commentsById[commentId]
      delete state.commentLikeUserIdsByCommentId[commentId]
      return undefined as T
    }

    if (pathname.endsWith('/like') && method === 'POST') {
      return undefined as T
    }

    if (pathname === '/me/notifications' && method === 'GET') {
      return [] as T
    }

    if (pathname === '/me/notifications/count' && method === 'GET') {
      return { unread: 0, count: 0 } as T
    }

    if (pathname === '/me/notifications/read' && method === 'PATCH') {
      return undefined as T
    }

    if (pathname === '/mapbox/init' && method === 'POST') {
      return { allowed: true } as T
    }

    if (pathname === '/me/cohort' && method === 'GET') {
      const user = requireAuthenticatedUser(state, token)
      const access = ensureCohortAccessForUser(state, user.id)

      if (!access || access.member_status === 'none') {
        return {
          cohort: null,
          member_status: 'none',
          created_at: access?.created_at ?? nowIso(),
          updated_at: access?.updated_at ?? null,
        } as T
      }

      const response: ApiMyCohortResponse = {
        cohort: {
          id: 'e2e-cohort',
          name: 'E2E Cohort',
          destination: user.onboarding_destination ?? 'Brisbane',
          departure_date: user.departure ?? nowIso(),
          status: 'active',
        },
        member_status: access.member_status,
        created_at: access.created_at,
        updated_at: access.updated_at,
      }
      return response as T
    }

    if (pathname === '/admin/stats' && method === 'GET') {
      const approvedCount = Object.values(state.usersById).filter(
        (user) => user.application_status === 'approved'
      ).length
      const pendingCount = Object.values(state.usersById).filter(
        (user) => user.application_status === 'pending'
      ).length
      const rejectedCount = Object.values(state.usersById).filter(
        (user) => user.application_status === 'rejected'
      ).length

      return {
        total_users: state.stats.user_count,
        pending_applications: pendingCount,
        approved_count: approvedCount,
        rejected_count: rejectedCount,
        waitlist_count: state.waitlist.length,
        cohort_size: state.communityMembers.filter((member) => member.status === 'selected').length,
      } as T
    }

    if (pathname === '/admin/applications' && method === 'GET') {
      const statusFilter = requestUrl.searchParams.get('status')?.trim() ?? ''
      const search = requestUrl.searchParams.get('search')?.trim().toLowerCase() ?? ''
      const page = parsePositiveInt(requestUrl.searchParams.get('page'), 1)
      const limit = parsePositiveInt(requestUrl.searchParams.get('limit'), 50)

      let filtered = Object.values(state.usersById)
      if (statusFilter) {
        filtered = filtered.filter((user) => user.application_status === statusFilter)
      }
      if (search) {
        filtered = filtered.filter((user) => {
          return (
            user.email.toLowerCase().includes(search) ||
            user.name.toLowerCase().includes(search) ||
            user.surname.toLowerCase().includes(search) ||
            user.onboarding_goal.toLowerCase().includes(search) ||
            user.onboarding_destination.toLowerCase().includes(search) ||
            user.origin_city.toLowerCase().includes(search) ||
            user.origin_country.toLowerCase().includes(search)
          )
        })
      }

      filtered.sort((a, b) => b.created_at.localeCompare(a.created_at))
      const total = filtered.length
      const start = (page - 1) * limit
      const items = filtered.slice(start, start + limit)

      return {
        items,
        total,
        page,
        limit,
      } as T
    }

    if (
      pathname.startsWith('/admin/applications/') &&
      pathname.endsWith('/status') &&
      method === 'PATCH'
    ) {
      const userId = pathname.split('/')[3]
      const user = state.usersById[userId]
      if (!user) throw createError(404, 'user not found')
      const body = parseBody<{ status?: string }>(options)
      user.application_status = body.status ?? user.application_status
      state.usersById[userId] = user
      return user as T
    }

    if (pathname === '/admin/cohort' && method === 'GET') {
      const members = state.communityMembers.map((member) => {
        const user = state.usersById[member.user_id]
        return {
          id: member.id,
          cohort_id: 'e2e-cohort',
          user_id: member.user_id,
          status: member.status,
          created_at: nowIso(),
          updated_at: null,
          name: member.name,
          surname: member.surname,
          email: user?.email ?? `${member.user_id}@example.com`,
          origin_city: member.origin_city,
          origin_country: user?.origin_country ?? '',
        }
      })

      return {
        cohort:
          members.length > 0
            ? {
                id: 'e2e-cohort',
                name: 'E2E Cohort',
                destination: 'Brisbane',
                max_participants: 100,
                status: 'active',
                created_at: nowIso(),
              }
            : null,
        members,
      } as T
    }

    if (pathname.startsWith('/admin/cohort/') && method === 'DELETE') {
      return undefined as T
    }

    if (pathname === '/admin/waitlist' && method === 'GET') {
      const page = parsePositiveInt(requestUrl.searchParams.get('page'), 1)
      const limit = parsePositiveInt(requestUrl.searchParams.get('limit'), 50)

      const allEntries = state.waitlist.map((entry, index) => ({
        id: `wait-${index + 1}`,
        email: entry.email,
        created_at: nowIso(),
        onboarding_id: entry.onboarding_id,
      }))
      const total = allEntries.length
      const start = (page - 1) * limit
      const items = allEntries.slice(start, start + limit)

      return {
        items,
        total,
        page,
        limit,
      } as T
    }

    return {} as T
  } finally {
    saveState(state)
  }
}
